import React, { useState } from 'react';
import { CheckCircle2, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { enrollmentSteps } from '../mock';
import { submitEnrollmentForm } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Enroll = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    affiliation: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submitEnrollmentForm(formData);
    if (result.success) {
      toast({
        title: "Application Submitted!",
        description: result.message,
      });
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        affiliation: '',
        message: ''
      });
    } else {
      toast({
        title: "Error",
        description: result.message,
        variant: "destructive"
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-[#302f2c] to-[#1a1c1b]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 text-center">
          <h1 className="font-black text-5xl md:text-8xl leading-[0.76] text-[#0A277A] uppercase mb-8">
            Join the Next Cohort
          </h1>
          <p className="text-gray-800 text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
            Take the first step toward a rewarding career in barbering. Applications are now open for our upcoming cohort.
          </p>
        </div>
      </section>

      {/* Enrollment Steps */}
      <section className="py-20">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Enrollment Process
            </h2>
            <p className="text-gray-600 text-lg">
              Four simple steps to begin your journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {enrollmentSteps.map((item) => (
              <div key={item.step} className="text-center">
                <div className="bg-[#3f4816] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-[#0A277A] font-black text-2xl">{item.step}</span>
                </div>
                <h3 className="font-black text-xl text-[#0A277A] uppercase mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Requirements */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-black text-3xl md:text-5xl text-[#0A277A] uppercase mb-8 text-center">
            Eligibility Requirements
          </h2>
          <Card className="bg-[#3f4816] border-none p-8">
            <ul className="space-y-4">
              <li className="flex items-start text-gray-800">
                <CheckCircle2 size={24} className="text-[#0A277A] mr-4 mt-0.5 flex-shrink-0" />
                <span>U.S. military veteran (all branches), military spouse, dependent (18+), or DoD civilian</span>
              </li>
              <li className="flex items-start text-gray-800">
                <CheckCircle2 size={24} className="text-[#0A277A] mr-4 mt-0.5 flex-shrink-0" />
                <span>Currently residing in or able to commute to the Kaiserslautern Military Community (KMC), Germany</span>
              </li>
              <li className="flex items-start text-gray-800">
                <CheckCircle2 size={24} className="text-[#0A277A] mr-4 mt-0.5 flex-shrink-0" />
                <span>Commitment to attend all 12 weeks of training (Monday-Friday)</span>
              </li>
              <li className="flex items-start text-gray-800">
                <CheckCircle2 size={24} className="text-[#0A277A] mr-4 mt-0.5 flex-shrink-0" />
                <span>Genuine interest in pursuing a career in barbering and entrepreneurship</span>
              </li>
              <li className="flex items-start text-gray-800">
                <CheckCircle2 size={24} className="text-[#0A277A] mr-4 mt-0.5 flex-shrink-0" />
                <span>Basic English proficiency (all instruction conducted in English)</span>
              </li>
            </ul>
          </Card>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-black text-3xl md:text-5xl text-[#0A277A] uppercase mb-4">
              Submit Your Application
            </h2>
            <p className="text-gray-600 text-lg">
              Fill out the form below and we'll be in touch within 48 hours
            </p>
          </div>

          <Card className="bg-gray-50 border border-gray-200 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                    First Name *
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000]"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                    Last Name *
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000]"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000]"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000]"
                />
              </div>

              <div>
                <Label htmlFor="affiliation" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                  Military Affiliation *
                </Label>
                <Select
                  value={formData.affiliation}
                  onValueChange={(value) => setFormData({ ...formData, affiliation: value })}
                  required
                >
                  <SelectTrigger className="bg-white border-gray-200 text-gray-800">
                    <SelectValue placeholder="Select your affiliation" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="veteran" className="text-gray-800">U.S. Military Veteran</SelectItem>
                    <SelectItem value="spouse" className="text-gray-800">Military Spouse</SelectItem>
                    <SelectItem value="dependent" className="text-gray-800">Military Dependent</SelectItem>
                    <SelectItem value="civilian" className="text-gray-800">DoD Civilian</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                  Tell Us About Yourself
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Why do you want to join MOS Academy? What are your career goals?"
                  className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000] placeholder:text-gray-600"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#D80000] text-[#1a1c1b] hover:opacity-90 rounded-full py-6 text-lg font-semibold uppercase transition-all hover:scale-105"
              >
                Submit Application
              </Button>

              <p className="text-gray-600 text-sm text-center">
                * Required fields. We respect your privacy and will never share your information.
              </p>
            </form>
          </Card>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-black text-3xl md:text-5xl text-[#0A277A] uppercase mb-8 text-center">
            What Happens Next?
          </h2>
          <div className="space-y-6">
            <Card className="bg-[#3f4816] border-none p-6 flex items-start space-x-4">
              <div className="bg-[#D80000] text-[#1a1c1b] font-black w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="font-bold text-[#0A277A] mb-2">Application Review</h3>
                <p className="text-gray-800 text-sm">Our team will review your application within 48 hours.</p>
              </div>
            </Card>

            <Card className="bg-[#3f4816] border-none p-6 flex items-start space-x-4">
              <div className="bg-[#D80000] text-[#1a1c1b] font-black w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="font-bold text-[#0A277A] mb-2">Interview Invitation</h3>
                <p className="text-gray-800 text-sm">If you meet the requirements, we'll schedule a one-on-one interview to discuss your goals.</p>
              </div>
            </Card>

            <Card className="bg-[#3f4816] border-none p-6 flex items-start space-x-4">
              <div className="bg-[#D80000] text-[#1a1c1b] font-black w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="font-bold text-[#0A277A] mb-2">Acceptance & Enrollment</h3>
                <p className="text-gray-800 text-sm">Once accepted, you'll receive enrollment paperwork and cohort start date information.</p>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enroll;